import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges, SimpleChanges,
    ViewChild,
} from '@angular/core';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import { ItxCustomEvent } from '@features/cms-render/domain/models/itx-custom-event.model';
import { CmsRenderService } from '@features/cms-render/ui/services/cms-render/cms-render.service';
import { getLastSlugWithoutParams } from '@frontend/common/utils';

@Component({
    selector: 'render-wysiwyg',
    templateUrl: './render-wysiwyg.component.html',
    styleUrls: ['./render-wysiwyg.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RenderWysiwygComponent implements AfterViewInit, OnChanges {
    @ViewChild('wysiwygText', { read: ElementRef, static: false }) public wysiwygText: ElementRef;

    @Input() public element: ContentItemTree;

    @Input() public html: string | undefined;

    @Input() public textPaddingTop: string = '0%';

    @Input() public textPaddingBottom: string = '0%';

    @Input() public textPaddingLeft: string = '0%';

    @Input() public textPaddingRight: string = '0%';

    public linkType: string;

    constructor(
        private readonly cmsRenderService: CmsRenderService,
    ) {}

    @BrowserOnly<RenderWysiwygComponent, undefined>(undefined)
    public ngAfterViewInit(): void {
        this.initEvents();
    }

    @BrowserOnly<RenderWysiwygComponent, undefined>(undefined)
    public ngOnChanges(_: SimpleChanges) {
        this.textPaddingTop = this.toUnit(this.textPaddingTop);
        this.textPaddingBottom = this.toUnit(this.textPaddingBottom);
        this.textPaddingLeft = this.toUnit(this.textPaddingLeft);
        this.textPaddingRight = this.toUnit(this.textPaddingRight);
    }

    private isInitializedEvents: boolean = false;

    @BrowserOnly<RenderWysiwygComponent, undefined>(undefined)
    public initEvents(): void {
        if (!this.isInitializedEvents) {
            const el: HTMLDivElement = this.wysiwygText.nativeElement;
            const links = el.querySelectorAll('a');
            links.forEach((link) => {
                link.setAttribute('aria-label', getLastSlugWithoutParams(link.href));

                link.addEventListener('click', (event) => {
                    this.onLinkClick(link, event);
                });
            });

            const buttonElements = el.querySelectorAll('button');
            buttonElements.forEach((button) => {
                button.addEventListener('click', () => {
                    this.onButtonClick(button);
                });
            });
            this.isInitializedEvents = true;
        }
    }

    public onButtonClick(button: HTMLElement): void {
        const linkUrl = button.getAttribute('data-link');
        this.goToRoute(linkUrl, '', '', '', false);
    }

    public onLinkClick(link: HTMLElement, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        const target = link.getAttribute('target');
        const href = link.getAttribute('href');
        const cmsLink = link.getAttribute('cms-link');
        const src = cmsLink.includes('customEvent') ? cmsLink : href;
        const relativeLink = link.getAttribute('data-relative-link') === 'true';

        const categoryId = link.getAttribute('data-category-id');
        const productId = link.getAttribute('data-product-id');
        const type = link.getAttribute('data-link-type');
        const section = link.getAttribute('data-section');

        switch (type) {
            case 'category':
                this.openCategory(categoryId, target);
                break;
            case 'menu':
                this.openMenu(categoryId);
                break;
            case 'product':
                this.openProduct(productId, categoryId);
                break;
            case 'section':
                this.openSection(categoryId, type, section, target);
                break;
            default:
                this.goToRoute(src, categoryId, type, target, relativeLink);
                break;
        }
    }

    public openProduct(_productId: string, _categoryId: string): void {
        this.cmsRenderService.openShopTheLookModal(_productId, _categoryId);
    }

    public openCategory(categoryId: string, target: string): void {
        this.cmsRenderService.dispatchCategoriesEvents('', categoryId, 'category', '', target === '_blank');
    }

    public openMenu(categoryId: string): void {
        this.cmsRenderService.dispatchMenuEvents(categoryId, 'menu', '');
    }

    public openSection(categoryId: string, type: string, section: string, target: string): void {
        this.cmsRenderService.dispatchSectionEvents(categoryId, type, '', section, target === '_blank', this.element.slug);
    }

    private toUnit(value: number | string): string {
        return typeof value === 'number' ? `${value}%` : value;
    }

    public goToRoute(
        src: string,
        categoryId: string,
        type: string,
        target: string,
        relativeLink: boolean,
    ) {
        if (src.includes('customEvent')) {
            const myEvent: ItxCustomEvent = {
                detail: {
                    extra: {
                        type: 'external',
                        src,
                    },
                },
            };
            this.cmsRenderService.dispatchManagement(myEvent);
        }
        this.cmsRenderService.dispatchExternalEvent(categoryId, type, '', src, target === '_blank', this.element.slug, relativeLink);
    }
}
